export const formsProbeSelectors = {
  floatingField: ".form-field",
  floatingInput: ".form-field input, .form-field select, .form-field textarea",
  introSourceSelect: "#user_intro_source",
  introOtherText: "#intro-other-text",
  userOrganizationSelect: "#user_organization_id",
  organizationSelect: "#organization_id",
  otherOrgText: "#other-org-text",
  avatarInput: "#avatar-image",
  avatarButton: ".avatar-file",
  avatarSubmit: ".submit-image",
  avatarFileName: ".file-name",
  referralAddButton: ".side-add-button",
  referralRemoveButton: ".side-remove-button",
} as const;

export type FormsProbeSelectorName = keyof typeof formsProbeSelectors;

export type FormsProbeState = {
  readonly floatingFieldCount: number;
  readonly floatingInputCount: number;
  readonly visibilityControlCount: number;
  readonly avatarUploadPresent: boolean;
  readonly referralControlsPresent: boolean;
  readonly currentRuntime: "legacy-asset-builder";
  readonly nextRuntime: "esbuild-module";
};

export function formsProbeState(root: ParentNode = document): FormsProbeState {
  const visibilityControlCount = [
    formsProbeSelectors.introSourceSelect,
    formsProbeSelectors.userOrganizationSelect,
    formsProbeSelectors.organizationSelect,
  ].filter((selector) => root.querySelector(selector)).length;

  return {
    floatingFieldCount: root.querySelectorAll(formsProbeSelectors.floatingField).length,
    floatingInputCount: root.querySelectorAll(formsProbeSelectors.floatingInput).length,
    visibilityControlCount,
    avatarUploadPresent:
      Boolean(root.querySelector(formsProbeSelectors.avatarInput)) &&
      Boolean(root.querySelector(formsProbeSelectors.avatarSubmit)),
    referralControlsPresent:
      Boolean(root.querySelector(formsProbeSelectors.referralAddButton)) ||
      Boolean(root.querySelector(formsProbeSelectors.referralRemoveButton)),
    currentRuntime: "legacy-asset-builder",
    nextRuntime: "esbuild-module",
  };
}

export function markFormsProbeReady(documentRef: Document = document): FormsProbeState {
  const state = formsProbeState(documentRef);
  const hasTrackedSurface =
    state.floatingFieldCount > 0 ||
    state.visibilityControlCount > 0 ||
    state.avatarUploadPresent ||
    state.referralControlsPresent;

  documentRef.documentElement.dataset.formsProbe = hasTrackedSurface ? "observed" : "absent";
  documentRef.documentElement.dataset.formsProbeFloatingFields = String(state.floatingFieldCount);
  documentRef.documentElement.dataset.formsProbeVisibilityControls = String(state.visibilityControlCount);

  const avatarInput = documentRef.querySelector<HTMLInputElement>(formsProbeSelectors.avatarInput);
  if (avatarInput) {
    avatarInput.dataset.esbuildFormsProbe = state.avatarUploadPresent ? "ready" : "missing-targets";
  }

  return state;
}

export function installFormsProbe(documentRef: Document = document): void {
  const markReady = (): void => {
    markFormsProbeReady(documentRef);
  };

  if (documentRef.readyState === "loading") {
    documentRef.addEventListener("DOMContentLoaded", markReady, { once: true });
  } else {
    markReady();
  }

  documentRef.addEventListener("turbo:load", markReady);
}
